import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Tab,
  Tabs,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Slider,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Chip,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Avatar,
  CardActions,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  AutoStories as StoryIcon,
  Send as SendIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  GroupWork as MultiAgentIcon,
  Public as WorldIcon,
  People as CharactersIcon,
  Timeline as PlotIcon,
  Chat as DialogueIcon,
  Psychology as ThemeIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { storyService } from '../services/storyService';
import { StorySession, StoryIteration } from '../models/story';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';

// TabPanel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

const genres = [
  'Fantasy', 'Science Fiction', 'Mystery', 'Thriller', 'Romance',
  'Horror', 'Historical Fiction', 'Literary Fiction', 'Young Adult',
  'Children\'s', 'Comedy', 'Drama', 'Adventure', 'Dystopian'
];

const pointsOfView = [
  'First Person', 'Third Person Limited', 'Third Person Omniscient',
  'Second Person', 'Multiple POV'
];

const tones = [
  'Serious', 'Humorous', 'Dark', 'Light-hearted', 'Suspenseful',
  'Romantic', 'Satirical', 'Nostalgic', 'Inspirational'
];

const StoryGenerator: React.FC = () => {
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Main state
  const [tabValue, setTabValue] = useState(0);
  const [sessions, setSessions] = useState<StorySession[]>([]);
  const [currentSession, setCurrentSession] = useState<StorySession | null>(null);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // New story form states
  const [starterPrompt, setStarterPrompt] = useState('');
  const [themes, setThemes] = useState('');
  const [topics, setTopics] = useState('');
  const [pointOfView, setPointOfView] = useState('');
  const [tone, setTone] = useState('');
  const [genre, setGenre] = useState('');
  const [temperature, setTemperature] = useState(0.8);
  const [maxTokens, setMaxTokens] = useState(2000);
  const [multiAgentMode, setMultiAgentMode] = useState(false);
  
  // Multi-agent specific fields
  const [premise, setPremise] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [storyLength, setStoryLength] = useState('');
  
  // Current session iteration
  const [feedback, setFeedback] = useState('');
  const [iterationTab, setIterationTab] = useState(0);

  // Backend session retrieval
  const {
    sessions: backendSessions,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'StorySession',
    sessionBased: true,
    autoFetch: true,
  });

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = () => {
    const sessionIds = storyService.getAllSessionIds();
    const loadedSessions: StorySession[] = sessionIds.map(id => {
      const sessionData = storyService.getSessionFromLocalStorage(id);
      return sessionData;
    }).filter(Boolean);

    setSessions(loadedSessions);
    if (loadedSessions.length > 0 && !currentSession) {
      setCurrentSession(loadedSessions[0]);
    }
  };

  // Merge backend sessions with local data
  useEffect(() => {
    if (backendSessions && backendSessions.length > 0) {
      const converted: StorySession[] = backendSessions.map(bs => {
        // Parse sessionData JSON string to extract story data
        let sessionData: any = {};
        try {
          if (bs.session.sessionData) {
            sessionData = JSON.parse(bs.session.sessionData);
          }
        } catch (e) {
          console.error('[StoryGenerator] Failed to parse sessionData:', e);
        }

        return {
          id: bs.session.id,
          createdAt: bs.session.createdAt,
          lastUpdated: bs.session.lastUpdatedAt || bs.session.updatedAt || bs.session.createdAt,
          starterPrompt: sessionData.StarterPrompt || sessionData.starterPrompt || sessionData.Premise || sessionData.premise || '',
          themes: sessionData.Themes || sessionData.themes || '',
          topics: sessionData.Topics || sessionData.topics || '',
          pointOfView: sessionData.PointOfView || sessionData.pointOfView || '',
          tone: sessionData.Tone || sessionData.tone || '',
          genre: sessionData.Genre || sessionData.genre || '',
          targetAudience: sessionData.TargetAudience || sessionData.targetAudience || '',
          iterations: sessionData.Iterations || sessionData.iterations || [],
          isActive: sessionData.IsActive ?? sessionData.isActive ?? false,
          multiAgentData: sessionData.MultiAgentData || sessionData.multiAgentData
        };
      });

      const backendIds = new Set(converted.map(s => s.id));
      const localOnly = sessions.filter(s => !backendIds.has(s.id));
      setSessions([...converted, ...localOnly]);
    }
  }, [backendSessions]);

  const validateStoryForm = (): string | null => {
    if (multiAgentMode) {
      if (!premise.trim()) return 'Story premise is required';
    } else {
      if (!starterPrompt.trim()) return 'Story prompt is required';
    }
    return null;
  };

  const handleStartSession = async () => {
    const validation = validateStoryForm();
    if (validation) {
      setError(validation);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      if (multiAgentMode) {
        const response = await storyService.startMultiAgentSession({
          premise: premise || starterPrompt,
          genre: genre || undefined,
          targetAudience: targetAudience || undefined,
          length: storyLength || undefined,
          themes: themes || undefined,
          maxResponseTokens: maxTokens,
          temperature
        });
        
        // Create local session for multi-agent
        const newSession: StorySession = {
          id: response.sessionId,
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
          starterPrompt: premise,
          themes,
          genre,
          targetAudience,
          iterations: [{
            iterationNumber: 1,
            story: response.finalStory || 'Multi-agent story generated',
            timestamp: new Date().toISOString()
          }],
          isActive: true,
          multiAgentData: response
        };

        // Save to local storage
        storyService.saveSessionToLocalStorage(response.sessionId, newSession);
        setCurrentSession(newSession);
        setSessions([newSession, ...sessions]);
        
      } else {
        const response = await storyService.startSession({
          starterPrompt,
          themes: themes || undefined,
          topics: topics || undefined,
          pointOfView: pointOfView || undefined,
          tone: tone || undefined,
          genre: genre || undefined,
          maxTokensPerIteration: maxTokens,
          temperature
        });
        
        // Handle both response formats
        let iterations: StoryIteration[] = [];
        
        if (response.finalStory) {
          // Backend returns finalStory for single-agent mode
          iterations = [{
            iterationNumber: 1,
            story: response.finalStory,
            timestamp: new Date().toISOString()
          }];
        } else if (response.storyIterations) {
          // Handle array of iterations if provided
          iterations = response.storyIterations.map((story, index) => ({
            iterationNumber: index + 1,
            story,
            timestamp: new Date().toISOString()
          }));
        }
        
        const newSession: StorySession = {
          id: response.sessionId,
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
          starterPrompt,
          themes,
          topics,
          pointOfView,
          tone,
          genre,
          iterations,
          isActive: true
        };
        
        // Save to local storage
        storyService.saveSessionToLocalStorage(response.sessionId, newSession);
        setCurrentSession(newSession);
        setSessions([newSession, ...sessions]);
      }
      
      // Clear form and switch to active story tab
      clearForm();
      setTabValue(2);
      loadSessions();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to start story session');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueStory = async () => {
    if (!currentSession || !feedback.trim()) {
      setError('Please provide feedback to continue the story');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await storyService.iterate(currentSession.id, {
        feedback,
        maxTokensPerIteration: maxTokens,
        temperature
      });
      
      const newIteration: StoryIteration = {
        iterationNumber: response.iterationNumber || currentSession.iterations.length + 1,
        story: response.story || '',
        chapterTitle: response.chapterTitle,
        feedback,
        timestamp: new Date().toISOString()
      };

      const updatedSession = {
        ...currentSession,
        iterations: [...currentSession.iterations, newIteration],
        lastUpdated: new Date().toISOString()
      };
      
      setCurrentSession(updatedSession);
      setFeedback('');
      setIterationTab(updatedSession.iterations.length - 1);
      
      // Update local storage
      storyService.saveSessionToLocalStorage(currentSession.id, updatedSession);
      setSessions(sessions.map(s => s.id === updatedSession.id ? updatedSession : s));
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to continue story');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = (sessionId: string) => {
    storyService.deleteSessionFromLocalStorage(sessionId);
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
    }
    loadSessions();
  };

  const clearForm = () => {
    setStarterPrompt('');
    setPremise('');
    setThemes('');
    setTopics('');
    setPointOfView('');
    setTone('');
    setGenre('');
    setTargetAudience('');
    setStoryLength('');
    setTemperature(0.8);
    setMaxTokens(2000);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const exportSession = () => {
    if (currentSession) {
      const content = JSON.stringify(currentSession, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `story-${currentSession.id}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
          }}
        >
          <StoryIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Story Generator' : 'Story Generator Studio'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'AI-powered story creation' : 'Create compelling stories with AI-powered assistance'}
        </Typography>
      </Box>

      {/* Main Tab Navigation */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }} elevation={0}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 48
            }
          }}
        >
          <Tab label="Sessions" />
          <Tab label={isMobile ? 'New' : 'Create New'} />
          {currentSession && <Tab label={isMobile ? 'Active' : 'Active Story'} />}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        {/* Sessions List */}
        {sessions.length === 0 ? (
          <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              No stories found
            </Typography>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={() => setTabValue(1)}
              size="large"
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44,
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {isMobile ? 'Create Story' : 'Create Your First Story'}
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {sessions.map((session) => (
              <Grid item xs={12} md={6} key={session.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: currentSession?.id === session.id ? 2 : 1,
                    borderColor: currentSession?.id === session.id ? 'primary.main' : 'divider'
                  }}
                  onClick={() => {
                    setCurrentSession(session);
                    setTabValue(2);
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography 
                        variant="h6"
                        sx={{ 
                          fontSize: { xs: '1rem', sm: '1.125rem' },
                          flex: 1,
                          mr: 1
                        }}
                      >
                        {session.starterPrompt?.substring(0, isMobile ? 30 : 50)}...
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.id);
                        }}
                        color="error"
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      {session.genre && (
                        <Chip 
                          label={session.genre} 
                          size="small" 
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      )}
                      {session.tone && (
                        <Chip 
                          label={session.tone} 
                          size="small" 
                          variant="outlined" 
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      )}
                      {session.multiAgentData && (
                        <Chip 
                          label={isMobile ? 'Multi' : 'Multi-Agent'} 
                          size="small" 
                          color="secondary" 
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      )}
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      {session.themes && `${isMobile ? '' : 'Themes: '}${session.themes.substring(0, isMobile ? 60 : 100)}`}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {isMobile ? new Date(session.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `Created: ${new Date(session.createdAt).toLocaleDateString()}`} • 
                      {session.iterations.length} {isMobile ? 'iter' : `iteration${session.iterations.length !== 1 ? 's' : ''}`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Create New Story */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            {isMobile ? 'New Story' : 'Create New Story'}
          </Typography>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }} 
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Mode Toggle */}
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <FormControlLabel
              control={
                <Switch
                  checked={multiAgentMode}
                  onChange={(e) => setMultiAgentMode(e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!isMobile && <MultiAgentIcon />}
                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Multi-Agent Mode
                  </Typography>
                </Box>
              }
            />
            <Typography 
              variant="caption" 
              display="block" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
            >
              {multiAgentMode 
                ? (isMobile ? 'Multiple agents for complex stories' : 'Use multiple specialized agents for complex story development')
                : (isMobile ? 'Single agent for focused stories' : 'Use a single agent for focused story generation')
              }
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 3 : 4}
                label={multiAgentMode ? 'Story Premise *' : 'Story Prompt *'}
                value={multiAgentMode ? premise : starterPrompt}
                onChange={(e) => multiAgentMode ? setPremise(e.target.value) : setStarterPrompt(e.target.value)}
                placeholder={multiAgentMode 
                  ? (isMobile ? "Describe story concept..." : "Describe your story concept, setting, and main conflict...")
                  : (isMobile ? "Start your story..." : "Start your story with a compelling opening or describe what you want to write about...")
                }
                error={multiAgentMode ? !premise.trim() : !starterPrompt.trim()}
                helperText={multiAgentMode ? !premise.trim() ? "Story premise is required" : (isMobile ? "Core concept and conflict" : "The core concept and conflict of your story")
                  : !starterPrompt.trim() ? "Story prompt is required" : (isMobile ? "Story's beginning or concept" : "Your story's beginning or main concept")}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Genre</InputLabel>
                <Select
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  label="Genre"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <em>None</em>
                  </MenuItem>
                  {genres.map((g) => (
                    <MenuItem key={g} value={g} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {g}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              {multiAgentMode ? (
                <TextField
                  fullWidth
                  label="Target Audience"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder={isMobile ? "e.g., Young adults" : "e.g., Young adults, Children, General audience"}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Tone</InputLabel>
                  <Select
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    label="Tone"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <em>None</em>
                    </MenuItem>
                    {tones.map((t) => (
                      <MenuItem key={t} value={t} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Themes"
                value={themes}
                onChange={(e) => setThemes(e.target.value)}
                placeholder={isMobile ? "e.g., Love, betrayal" : "e.g., Love, betrayal, redemption"}
                helperText={isMobile ? "Key themes" : "Key themes to explore in your story"}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {multiAgentMode ? (
                <TextField
                  fullWidth
                  label="Story Length"
                  value={storyLength}
                  onChange={(e) => setStoryLength(e.target.value)}
                  placeholder={isMobile ? "e.g., Short story" : "e.g., Short story, Novella, Full novel"}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {isMobile ? 'POV' : 'Point of View'}
                  </InputLabel>
                  <Select
                    value={pointOfView}
                    onChange={(e) => setPointOfView(e.target.value)}
                    label={isMobile ? 'POV' : 'Point of View'}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <em>None</em>
                    </MenuItem>
                    {pointsOfView.map((pov) => (
                      <MenuItem key={pov} value={pov} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {pov}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

            {!multiAgentMode && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Topics"
                  value={topics}
                  onChange={(e) => setTopics(e.target.value)}
                  placeholder={isMobile ? "e.g., Space exploration" : "e.g., Space exploration, medieval kingdoms, modern day"}
                  helperText={isMobile ? "Specific topics" : "Specific topics or settings to include"}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={multiAgentMode ? 12 : 6}>
              <TextField
                fullWidth
                type="number"
                label="Max Tokens"
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value) || 2000)}
                InputProps={{ 
                  inputProps: { min: 500, max: 4000 },
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                helperText={isMobile ? "Max content length" : "Maximum length of generated content"}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 5 } }}>
                <Typography 
                  variant="body2" 
                  gutterBottom
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {isMobile ? 'Creativity: ' : 'AI Creativity Level: '}{temperature}
                </Typography>
                <Slider
                  value={temperature}
                  onChange={(_, value) => setTemperature(value as number)}
                  min={0}
                  max={1}
                  step={0.1}
                  marks={[
                    { value: 0, label: isMobile ? 'Cons.' : 'Conservative' },
                    { value: 0.5, label: 'Balanced' },
                    { value: 1, label: 'Creative' }
                  ]}
                  valueLabelDisplay="auto"
                  sx={{ 
                    ml: { xs: 1, sm: 2 }, 
                    mr: { xs: 1, sm: 4 },
                    '& .MuiSlider-markLabel': {
                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={handleStartSession}
            disabled={loading || validateStoryForm() !== null}
            startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <AddIcon />)}
            sx={{ 
              mt: { xs: 2, sm: 3 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44
            }}
          >
            {loading 
              ? (isMobile ? 'Creating...' : 'Creating Story...') 
              : (isMobile 
                ? `Create ${multiAgentMode ? 'Multi' : 'Single'}` 
                : `Create ${multiAgentMode ? 'Multi-Agent' : 'Single-Agent'} Story`)
            }
          </Button>
        </Paper>
      </TabPanel>

      {currentSession && (
        <TabPanel value={tabValue} index={2}>
          {/* Active Story */}
          <Box>
            {/* Session Header */}
            <Card sx={{ mb: { xs: 2, sm: 3 } }}>
              <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography 
                    variant="h5"
                    sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                  >
                    {isMobile ? 'Active' : 'Active Story'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Export Story">
                      <IconButton 
                        onClick={exportSession}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {currentSession.genre && (
                    <Chip 
                      label={currentSession.genre} 
                      size="small" 
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                  {currentSession.tone && (
                    <Chip 
                      label={currentSession.tone} 
                      size="small" 
                      variant="outlined" 
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                  {currentSession.multiAgentData && (
                    <Chip 
                      label={isMobile ? 'Multi' : 'Multi-Agent'} 
                      size="small" 
                      color="secondary" 
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  )}
                </Box>

                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                >
                  <strong>Prompt:</strong> {currentSession.starterPrompt}
                </Typography>
                {currentSession.themes && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    <strong>Themes:</strong> {currentSession.themes}
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Story Content */}
            {currentSession.iterations.length > 0 && (
              <Paper sx={{ mb: { xs: 2, sm: 3 } }}>
                <Tabs
                  value={iterationTab}
                  onChange={(_, newValue) => setIterationTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ 
                    borderBottom: 1, 
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      minHeight: 48
                    }
                  }}
                >
                  {currentSession.iterations.map((iteration, index) => (
                    <Tab
                      key={index}
                      label={iteration.chapterTitle || (isMobile ? `Ch. ${iteration.iterationNumber}` : `Chapter ${iteration.iterationNumber}`)}
                    />
                  ))}
                </Tabs>

                {currentSession.iterations.map((iteration, index) => (
                  <TabPanel key={index} value={iterationTab} index={index}>
                    <Box sx={{ p: { xs: 2, sm: 3 } }}>
                      {iteration.feedback && (
                        <Alert 
                          severity="info" 
                          sx={{ 
                            mb: 2,
                            fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                          }}
                        >
                          <Typography 
                            variant="body2"
                            sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                          >
                            <strong>Feedback:</strong> {iteration.feedback}
                          </Typography>
                        </Alert>
                      )}
                      
                      <Box sx={{ 
                        '& p': { 
                          margin: '8px 0', 
                          lineHeight: 1.7,
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }, 
                        '& h1, & h2, & h3, & h4, & h5, & h6': { 
                          margin: '16px 0 8px 0', 
                          fontWeight: 600,
                          fontSize: { xs: '1rem', sm: '1.25rem' }
                        },
                        '& ul, & ol': { 
                          margin: '8px 0', 
                          paddingLeft: '20px',
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        },
                        '& li': { margin: '4px 0' }
                      }}>
                        <ReactMarkdown>{iteration.story}</ReactMarkdown>
                      </Box>

                      <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          startIcon={!isMobile && <CopyIcon />}
                          onClick={() => copyToClipboard(iteration.story)}
                          sx={{ 
                            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                            minHeight: 44,
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          Copy
                        </Button>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            alignSelf: 'center',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                          }}
                        >
                          {new Date(iteration.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </TabPanel>
                ))}
              </Paper>
            )}

            {/* Continue Story */}
            {!currentSession.multiAgentData && (
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                >
                  {isMobile ? 'Continue' : 'Continue Story'}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 2 : 3}
                  label="Story Direction"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={isMobile ? "How to continue..." : "Describe how you want the story to continue..."}
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <SendIcon />)}
                  onClick={handleContinueStory}
                  disabled={loading || !feedback.trim()}
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 44,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  {loading ? 'Generating...' : (isMobile ? 'Continue' : 'Continue Story')}
                </Button>
              </Paper>
            )}
          </Box>
        </TabPanel>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)} 
          sx={{ 
            mt: 2,
            fontSize: { xs: '0.8125rem', sm: '0.875rem' }
          }}
        >
          {error}
        </Alert>
      )}
    </Container>
  );
};

export default StoryGenerator;